<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { usePrimeVue } from 'primevue/config';
import AppLayout from '@/layouts/AppLayout.vue';
import BlankLayout from '@/layouts/BlankLayout.vue';
import { useAuthStore } from '@/stores/auth';
import type { Locale } from '@qb/shared';
import { primeLocaleFor } from '@/locales/primevue';

const route = useRoute();
const authStore = useAuthStore();

// Keep PrimeVue's locale (Calendar month/day names + button labels) in sync with
// the app language — PrimeVue reads from its own config, not vue-i18n.
const primevue = usePrimeVue();
const { locale } = useI18n();
watch(
  () => locale.value,
  (next) => {
    // Merge (not replace) so PrimeVue's other default locale fields survive.
    Object.assign(primevue.config.locale ?? {}, primeLocaleFor(next as Locale));
  },
  { immediate: true },
);

/** Pick layout from route meta. `blank` = no aside (login). default = AppLayout. */
const currentLayout = computed(() =>
  route.meta?.layout === 'blank' ? BlankLayout : AppLayout,
);

/** Try to restore session on first mount. After this finishes, router guards
 *  can read authStore.isAuthenticated reliably. */
onMounted(() => {
  void authStore.restoreSession();
});
</script>

<template>
  <component :is="currentLayout" />
</template>
